import { LoadingSpinner, Stack } from '@amboss/design-system';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useSocketEmit } from '../../hooks/useSocketEmit';
import type { CreditType } from '../../interfaces/credit';
import type { Profile } from '../../interfaces/profile';
import {
  courseCertificateDataSelector,
  courseCertificateLoadingSelector,
  resetCourseCertificate,
  setCourseCertificateData,
  setCourseCertificateError,
  setCourseCertificateLoading,
} from '../../store/courseCertificate/courseCertificateSlice';

const CourseCertificate = React.lazy(() =>
  import('./CourseCertificate/CourseCertificate').then((m) => ({
    default: m.CourseCertificate,
  })),
);

type CourseCertificateModalProps = {
  courseId: string;
};

type GetCourseDetailsResponse = {
  firstName: string;
  lastName: string;
  courseTitle: string;
  courseSubtitle: string;
  courseCompletionDate: string;
  cmePoints: number;
  courseType: CreditType.CCCR | CreditType.E2I;
  clinicalRole: Profile['clinicalRole'];
};

const CourseCertificateModal = ({ courseId }: CourseCertificateModalProps) => {
  const dispatch = useAppDispatch();
  const { getCourseDetails } = useSocketEmit();
  const { t } = useTranslation();
  const data = useAppSelector(courseCertificateDataSelector);
  const loading = useAppSelector(courseCertificateLoadingSelector);

  useEffect(() => {
    dispatch(setCourseCertificateLoading(true));
    getCourseDetails(courseId)
      .then((data: GetCourseDetailsResponse) => dispatch(setCourseCertificateData(data)))
      .catch((err: any) =>
        dispatch(setCourseCertificateError(err?.message ?? 'Failed to load certificate')),
      )
      .finally(() => dispatch(setCourseCertificateLoading(false)));

    return () => {
      dispatch(resetCourseCertificate());
    };
  }, [courseId]);

  return (
    <Suspense fallback={null}>
      {loading || !data ? (
        <Stack height='100%' vAlignItems='center'>
          <LoadingSpinner screenReaderText={t('general.loadingAccessibilityLabel')} />
        </Stack>
      ) : (
        <CourseCertificate
          firstName={data.firstName}
          lastName={data.lastName}
          courseTitle={data.courseTitle}
          courseSubtitle={data.courseSubtitle}
          completionDate={data.courseCompletionDate}
          credits={data.cmePoints}
          courseType={data.courseType}
          clinicalRole={data.clinicalRole}
        />
      )}
    </Suspense>
  );
};

export default CourseCertificateModal;
